import { Grid, Stack } from "@mui/material";
import { MprcIconButtonMoreOptionsDropdown, MprcText } from "components";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { EnrollmentViewDto, TrainingType } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useCallback, useState } from 'react';
import { useTranslation } from "react-i18next";
import { TrainingBookmarkButton } from "views/lms/components/TrainingBookmarkButton";
import { TrainingLikeButton } from "views/lms/components/TrainingLikeButton";
import { TrainingShareButton } from "views/lms/components/TrainingShareButton";
import { TrainingRegistrationDialog } from "views/lms/student/Registration/TrainingRegistrationDialog";
import { useEnrollmentViewContext } from "..";
import { LpEnrollmentViewAbout } from "../LearnPath/LpEnrollmentViewAbout";
import { DoCancelEnrollment } from "./DoCancelEnrollment";
import { DoCloseEnrollment } from "./DoCloseEnrollment";
import { ReEnrollAction } from "./EnrollmentActions";
import { ProgressDetailsDialog } from "./ProgressDetailDialog";
import { ScoreDetailsDialog } from "./ScoreDetailDialog";

/////////////////////////////////////////////////////////////////////////////////////

type EnrollmentMenuOptions = "overview" | "discussion" | "notices" | "trainingInfo" | "progressDetail" | "scoreDetail" | "reEnroll" | "enrollSubordinates" | "unsubscribe" | "closeReproved";

interface IProps {
    enrollment: EnrollmentViewDto;
    readOnly?: boolean;
}

/////////////////////////////////////////////////////////////////////////////////////

export const TrainingInteractions = ({ readOnly }: IProps) => {

    const { t } = useTranslation("lms");
    const { enrollment, onReload, onChangeEnrollment } = useEnrollmentViewContext();

    const [dialog, setDialog] = useState<any>();

    const [currentAction, setCurrentAction] = useState<"cancel" | "close" | null>(null);

    /////////////////////////////////////////////////////////////////////////////////////
    ///
    const getEnrollmentMenuItems = useCallback(
        (): MprcDropdownItems<EnrollmentMenuOptions> => {

            const group0: MprcDropdownItems<EnrollmentMenuOptions> = [];

            if (enrollment.trainingType == TrainingType.LearningPath) {
                group0.push({ text: t("Student.EnrollmentView.OverviewAction"), value: "overview" });

                if (enrollment.discussionEnable)
                    group0.push({ text: t("Student.EnrollmentView.DiscussionAction"), value: "discussion" });

                if (enrollment.hasNotices)
                    group0.push({ text: t("Student.EnrollmentView.NoticesAction"), value: "notices" });
            }

            const group1: MprcDropdownItems<EnrollmentMenuOptions> = [
                { text: t("Student.EnrollmentView.TrainingInfoAction"), value: "trainingInfo" },
            ];

            if (enrollment.trainingType != TrainingType.ExternalCourse) {
                group1.push({ text: t("Student.EnrollmentView.ProgressDetailAction"), value: "progressDetail" });
            }

            if (enrollment.hasScore)
                group1.push({ text: t("Student.EnrollmentView.ScoreDetailAction"), value: "scoreDetail" });

            const group2: MprcDropdownItems<EnrollmentMenuOptions> = [];
            if (enrollment.canReEnroll)
                group2.push({ text: t("Student.EnrollmentView.ReEnrollAction"), value: "reEnroll" });

            if (enrollment.canEnrollSubordinates)
                group2.push({ text: t("Student.EnrollmentView.EnrollSubordinatesAction"), value: "enrollSubordinates" });

            const group3: MprcDropdownItems<EnrollmentMenuOptions> = [];
            if (enrollment.canCancel)
                group3.push({ text: <MprcText color="error">{t("Student.EnrollmentView.UnsubscribeAction")}</MprcText>, value: "unsubscribe" });

            if (enrollment.canForceFinish && enrollment.trainingType == TrainingType.LearningPath) {
                group3.push({ text: <MprcText color="error">{t("Student.EnrollmentView.CloseReprovedAction")}</MprcText>, value: "closeReproved" });
            }

            return MprcDropdownBuildGroupedItems<EnrollmentMenuOptions>([group0, group1, group2, group3]);
        }
        , [enrollment]
    );

    ///
    const handleSelectOption = (option: EnrollmentMenuOptions) => {
        switch (option) {
            case "trainingInfo":
                setDialog(
                    <TrainingRegistrationDialog
                        trainingId={enrollment.trainingId}
                        readonly
                        onClose={() => setDialog(null)}
                    />
                );
                break;
            case "overview":
            case "discussion":
                setDialog(
                    <LpEnrollmentViewAbout
                        aboutTab={option}
                        onClose={() => setDialog(null)}
                    />
                );
                break;
            case "progressDetail":
                setDialog(<ProgressDetailsDialog
                    enrollmentId={enrollment.enrollmentId}
                    onClose={() => setDialog(null)}
                />
                );
                break;
            case "scoreDetail":
                setDialog(<ScoreDetailsDialog
                    enrollmentId={enrollment.enrollmentId}
                    onClose={() => setDialog(null)}
                />
                );
                break;
            case "enrollSubordinates":
                setDialog(<TrainingRegistrationDialog
                    trainingId={enrollment.trainingId}
                    mode="userManager"
                    onClose={() => {
                        setDialog(null);
                    }}
                />);
                break;
            case "reEnroll":
                setDialog(<ReEnrollAction onClose={() => setDialog(null)} />);
                break;
            case "unsubscribe":
                setCurrentAction("cancel");
                break;
            case "closeReproved":
                setCurrentAction("close");
                break;
            default:
                enqueueSnackbar("Não implementado");
                break;
        }
    };

    return (
        <>
            {dialog}
            <Grid container justifyContent="space-between">
                <Grid>
                    {enrollment.enableLike &&
                        <TrainingLikeButton
                            trainingId={enrollment.trainingId}
                            readonly={readOnly ?? false}
                            liked={enrollment.liked}
                            count={enrollment.likeCount}
                            onChange={(liked, likeCount) => onChangeEnrollment({ liked, likeCount })}
                        />
                    }
                </Grid>
                <Grid>
                    <Stack direction="row">
                        <TrainingBookmarkButton
                            trainingId={enrollment.trainingId}
                            bookmarked={enrollment.bookmarked}
                            onChange={(bookmarked) => onChangeEnrollment({ bookmarked })}
                            readonly={readOnly ?? false}
                        />
                        <TrainingShareButton
                            trainingId={enrollment.trainingId}
                            trainingName={enrollment.trainingName}
                            shareOption={enrollment.shareOption}
                            trainingHasAudienceDefined={enrollment.hasAudienceDefined}
                            enableShareInChannelAndLists={enrollment.enableShareInChannelAndLists}
                            readonly={readOnly}
                        />
                        <MprcIconButtonMoreOptionsDropdown
                            items={getEnrollmentMenuItems()}
                            onChange={handleSelectOption}
                            disabled={readOnly}
                        />
                    </Stack>
                </Grid>
            </Grid>

            {currentAction == "close" &&
                <DoCloseEnrollment
                    open
                    onAction={
                        (status) => {
                            if (status == "closed") {
                                onReload();
                            }
                            else if (status == "canceled") setCurrentAction(null);
                        }
                    }
                />
            }
            {currentAction == "cancel" &&
                <DoCancelEnrollment
                    open
                    enrollmentId={enrollment.enrollmentId}
                    onAction={
                        (status) => {
                            if (status != "canceling") setCurrentAction(null);
                        }
                    }
                />
            }
        </>
    );
};
